import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, ArrowUpRight, Check } from "lucide-react";
import PageShell from "./PageShell";
import Seo from "../components/Seo";
import { Reveal, useEnquiry } from "../lib/site-ui";
import {
  PRICING, COURSES, MEDIA, CONTACT, WHATSAPP_URL, TEL_URL, SEO_LINKS,
  ORG_JSONLD, breadcrumbJsonLd,
} from "../config/site";

const link = "text-white underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors";

const points = [
  ["Who it's for", "Aspiring pilots preparing for their DGCA ground examinations — whether you are starting from scratch or revising specific subjects before an attempt."],
  ["How classes work", "Sessions are scheduled around you. One-to-one coaching can be arranged at a location that works for the student in Mumbai, or you can attend a personalised session at the instructor's location. Timings stay flexible."],
  ["Personalised coaching", "Instead of being one face in a large hall, you get direct instructor interaction, individual doubt-solving and a preparation plan built around the subjects you find hardest."],
];

function Body() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      {/* Header */}
      <section className="relative isolate min-h-[70svh] flex items-end overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={MEDIA.mumbai} alt="AeroVaid Aviation Academy — DGCA ground classes in Mumbai" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/60" />
        </div>
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-8 pb-16 pt-32">
          <Reveal as="p" className="label-mono text-slate-400 mb-5">Mumbai, Maharashtra, India</Reveal>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white max-w-3xl leading-[1.04]">
            DGCA Ground Classes in Mumbai
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Personalised, concept-based DGCA ground coaching for aspiring pilots — delivered one-to-one in Mumbai and
            built around the way the examinations actually test you.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button onClick={() => openEnquiry({ mode: "Offline (Mumbai)" })} data-testid="mumbai-demo-btn"
              className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide">
              BOOK YOUR FREE DEMO CLASS
            </button>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
              className="border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide flex items-center justify-center gap-2">
              <MessageCircle className="w-4 h-4" /> WHATSAPP US
            </a>
          </div>
        </div>
      </section>

      {/* What we offer + points */}
      <section className="py-20 sm:py-28 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 space-y-14">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">What AeroVaid offers</h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              AeroVaid Aviation Academy is a Mumbai-based DGCA ground school focused on personalised preparation. We do
              not run crowded batches — instead, coaching is tailored to each student across all six DGCA subjects.
              Students outside the city can join the same preparation through our{" "}
              <Link to={SEO_LINKS.online} className={link}>online DGCA ground classes across India</Link>.
            </p>
          </Reveal>

          {points.map(([h, p]) => (
            <Reveal key={h}>
              <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">{h}</h2>
              <p className="text-slate-300 text-lg leading-relaxed">{p}</p>
            </Reveal>
          ))}

          {/* Subjects */}
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-6">Subjects taught</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {COURSES.map((c) => (
                <div key={c.id} className="flex items-center gap-3 text-slate-200">
                  <Check className="w-4 h-4 text-slate-500 shrink-0" /> {c.name}
                </div>
              ))}
            </div>
            <p className="mt-6 text-slate-400">
              See how each subject is taught on our <Link to="/#courses" className={link}>DGCA subjects overview</Link>.
            </p>
          </Reveal>

          {/* Pricing */}
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">Founding-batch pricing</h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Current founding-batch pricing is{" "}
              <span className="text-white font-semibold">{PRICING.currency}{PRICING.amount} {PRICING.unit}</span> — a
              premium, personalised alternative to expensive ground schools while staying accessible for aspiring
              pilots. {PRICING.note} Full details are on our{" "}
              <Link to="/#pricing" className={link}>pricing section</Link>.
            </p>
          </Reveal>

          {/* Why AeroVaid over self-study */}
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-5">Why not just self-study?</h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Many aspiring pilots try to prepare alone and lose months to unclear concepts and scattered material. A
              structured ground school gives you a clear plan, someone to ask when you are stuck, and preparation aimed
              squarely at the DGCA syllabus — so your study time actually moves you forward.
            </p>
          </Reveal>

          {/* How to enquire */}
          <Reveal>
            <div className="border border-white/10 bg-slate-900/40 p-8 sm:p-10">
              <h2 className="font-display text-2xl sm:text-3xl text-white tracking-tight mb-4">How to enquire</h2>
              <p className="text-slate-300 leading-relaxed mb-7">
                Start with a free demo class — no commitment. Speak directly with the instructor, then decide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => openEnquiry({ mode: "Offline (Mumbai)" })}
                  className="bg-white text-slate-950 font-semibold px-8 py-4 rounded-sm hover:bg-slate-200 transition-all hover:scale-[1.02] text-sm tracking-wide">
                  BOOK FREE DEMO
                </button>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-8 py-4 rounded-sm hover:bg-[#20bd5a] transition-colors text-sm tracking-wide">
                  <MessageCircle className="w-4 h-4" /> WHATSAPP
                </a>
                <a href={TEL_URL}
                  className="flex items-center justify-center gap-2 border border-white/25 text-white px-8 py-4 rounded-sm hover:bg-white/5 transition-colors text-sm tracking-wide">
                  <Phone className="w-4 h-4" /> CALL {CONTACT.phoneDisplay}
                </a>
              </div>
              <p className="mt-8 text-slate-400">
                <Link to="/" className="inline-flex items-center gap-1 hover:text-white transition-colors">
                  Back to AeroVaid home <ArrowUpRight className="w-4 h-4" />
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default function MumbaiPage() {
  const jsonLd = useMemo(() => [
    ORG_JSONLD,
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "DGCA Ground Classes in Mumbai", path: SEO_LINKS.mumbai },
    ]),
  ], []);
  return (
    <PageShell>
      <Seo
        title="DGCA Ground Classes in Mumbai | AeroVaid"
        description="Personalised DGCA ground classes in Mumbai for aspiring pilots. One-to-one, concept-based coaching across all six DGCA subjects. Book a free demo with AeroVaid."
        path={SEO_LINKS.mumbai}
        jsonLd={jsonLd}
      />
      <Body />
    </PageShell>
  );
}
